import React, { useState } from "react";
import { Crown, Download, Share2, X } from "lucide-react";
import { toast } from "sonner";

const W = 1080, H = 1350;
export const BOARD_NAMES = { uk: "UK Chart Three", us: "US Chart Three", sport: "Sport Three" };
const fmtWeek = (iso) => new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

const fit = (ctx, text, max, size) => {
  let s = size;
  ctx.font = `800 ${s}px Inter`;
  while (s > 60 && ctx.measureText(text).width > max) { s -= 6; ctx.font = `800 ${s}px Inter`; }
};

const crown = (ctx, cx, cy, w) => {
  const h = w * 0.62;
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, cy + h / 2);
  ctx.lineTo(cx - w / 2, cy - h / 4);
  ctx.lineTo(cx - w / 4, cy + h / 8);
  ctx.lineTo(cx, cy - h / 2);
  ctx.lineTo(cx + w / 4, cy + h / 8);
  ctx.lineTo(cx + w / 2, cy - h / 4);
  ctx.lineTo(cx + w / 2, cy + h / 2);
  ctx.closePath();
  ctx.fillStyle = "#E8C766"; ctx.fill();
  ctx.lineWidth = 6; ctx.strokeStyle = "#B8922E"; ctx.stroke();
};

const draw = async ({ nickname, board, week, points, accuracy, players }) => {
  await Promise.all([document.fonts.load("800 120px Inter"), document.fonts.load("600 40px Inter")]).catch(() => {});
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#2D4A3E"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(232,199,102,0.55)"; ctx.lineWidth = 4; ctx.strokeRect(48, 48, W - 96, H - 96);
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.font = "600 30px Inter"; ctx.letterSpacing = "8px";
  ctx.fillText("CURIOVAULT  ·  TRIVIA VAULT", W / 2, 150); ctx.letterSpacing = "0px";
  crown(ctx, W / 2, 330, 240);
  ctx.fillStyle = "#E8C766"; ctx.font = "800 64px Inter"; ctx.letterSpacing = "10px";
  ctx.fillText("WEEKLY CHAMPION", W / 2, 540); ctx.letterSpacing = "0px";
  ctx.fillStyle = "#FFFFFF"; fit(ctx, nickname, W - 200, 150);
  ctx.fillText(nickname, W / 2, 720);
  ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.font = "600 48px Inter";
  ctx.fillText(BOARD_NAMES[board], W / 2, 810);
  ctx.fillStyle = "rgba(255,255,255,0.65)"; ctx.font = "500 36px Inter";
  ctx.fillText(`Week of ${fmtWeek(week)}`, W / 2, 870);
  const stats = [[`${points}`, "points"], [`${accuracy}%`, "accuracy"], [`${players}`, players === 1 ? "player" : "players"]];
  stats.forEach(([v, l], i) => {
    const x = 210 + i * 330;
    ctx.fillStyle = "rgba(255,255,255,0.08)"; ctx.beginPath(); ctx.roundRect(x - 140, 940, 280, 190, 28); ctx.fill();
    ctx.fillStyle = "#FFFFFF"; ctx.font = "800 72px Inter"; ctx.fillText(v, x, 1040);
    ctx.fillStyle = "rgba(255,255,255,0.65)"; ctx.font = "600 30px Inter"; ctx.fillText(l, x, 1090);
  });
  ctx.fillStyle = "#FFFFFF"; ctx.font = "600 38px Inter";
  ctx.fillText("Think you can beat me? curiovault.co/trivia", W / 2, 1230);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

export const ChampionCardButton = ({ nickname, board, week, points, accuracy, players, label = "Champion card", testid }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const file = `curiovault-champion-${board}-${week}.png`;
  const text = `👑 I'm the CurioVault ${BOARD_NAMES[board]} Weekly Champion (week of ${fmtWeek(week)}). Think you can beat me? https://curiovault.co/trivia`;
  const open = async () => {
    setBusy(true);
    try {
      const blob = await draw({ nickname, board, week, points, accuracy, players });
      setPreview({ url: URL.createObjectURL(blob), blob });
    } finally { setBusy(false); }
  };
  const close = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };
  const share = async () => {
    const f = new File([preview.blob], file, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [f] })) {
      try { await navigator.share({ files: [f], text, title: "CurioVault Weekly Champion" }); } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success("Caption copied. Download the image and post it with the caption"));
    }
  };
  const Pill = "inline-flex items-center justify-center gap-2 font-ui font-semibold text-sm rounded-full px-4 py-2.5 transition-[background-color,transform] active:scale-[0.98]";
  return (
    <>
      <button data-testid={testid || `champion-card-btn-${board}`} onClick={open} disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#F6EBCB] border border-[#D9B65C] px-3 py-1 font-ui text-xs font-bold text-[#7A5A12] hover:bg-[#F1E2B4] disabled:opacity-60 transition-colors">
        <Crown className="w-3.5 h-3.5" /> {busy ? "Preparing…" : label}
      </button>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="champion-card-modal">
          <div className="bg-[#F4F1EA] rounded-[20px] p-4 sm:p-5 w-full max-w-sm max-h-[92vh] overflow-y-auto font-ui" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm text-[#161514]">Champion card · Instagram size</p>
              <button onClick={close} data-testid="champion-card-close" className="text-[#77716A] hover:text-[#161514]"><X className="w-4 h-4" /></button>
            </div>
            <img src={preview.url} alt={`${nickname} weekly champion card`} data-testid="champion-card-image" className="w-full rounded-[14px] border border-[#DDD5C7]" />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a href={preview.url} download={file} data-testid="champion-card-download" className={`${Pill} bg-[#161514] text-white hover:bg-black`}><Download className="w-4 h-4" /> Download</a>
              <button onClick={share} data-testid="champion-card-share" className={`${Pill} bg-[#2D4A3E] text-white hover:bg-[#243D33]`}><Share2 className="w-4 h-4" /> Share</button>
            </div>
            <p className="text-[11px] text-[#77716A] mt-3">1080 × 1350, ready for an Instagram post. On a phone, Share opens Instagram and your other apps.</p>
          </div>
        </div>
      )}
    </>
  );
};
